import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Trash2, Plus, Users, Eye, EyeOff, Layout, Globe, Timer, Copy, RefreshCw, Check, Lock, Upload, FileArchive, ExternalLink, Bell, MessageSquare, ChevronDown, ChevronUp, Square, CheckSquare, Server, Power, Loader2, Terminal, Download } from 'lucide-react';
import useAutoRefresh from '../hooks/useAutoRefresh';

const API = process.env.REACT_APP_API_URL;

export default function AdminPanel() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const token = user?.token;
    const isFullAdmin = user?.role === 'admin';
    const isLino = user?.username === 'Lino';

    const [tab, setTab] = useState('users');
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [bereiche, setBereiche] = useState([]);
    const [userVisits, setUserVisits] = useState([]);
    const [editingPerms, setEditingPerms] = useState(null);
    const [newUser, setNewUser] = useState({ username: '', password: '', permissions: [], maxLogins: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [siteLocked, setSiteLocked] = useState(false);
    const [legalMissing, setLegalMissing] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [countdown, setCountdown] = useState({ title: '', target: '' });
    const [countdownSaved, setCountdownSaved] = useState(false);
    const [previews, setPreviews] = useState([]);
    const [linkCopiedId, setLinkCopiedId] = useState(null);
    const [uploadMode, setUploadMode] = useState('new'); // 'new' | 'overwrite'
    const [uploadCode, setUploadCode] = useState('');
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadTargetId, setUploadTargetId] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadDone, setUploadDone] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [unreadFeedback, setUnreadFeedback] = useState([]);
    const [expandedFeedbackId, setExpandedFeedbackId] = useState(null);
    const [feedbackByPreview, setFeedbackByPreview] = useState({});
    const [replyText, setReplyText] = useState({});
    const [replySending, setReplySending] = useState({});
    const [settingUpId, setSettingUpId] = useState(null);
    const [appError, setAppError] = useState({});
    const [expandedLogId, setExpandedLogId] = useState(null);
    const [inviteCode, setInviteCode] = useState('');
    const [codeCopied, setCodeCopied] = useState(false);
    const [invitePermissions, setInvitePermissions] = useState([]);
    const [invitePermSaved, setInvitePermSaved] = useState(false);
    const [editingPw, setEditingPw] = useState(null); // { id, value, show }
    const [pwSaved, setPwSaved] = useState(null);
    const [showDeactivated, setShowDeactivated] = useState(false);
    const [showNewUserForm, setShowNewUserForm] = useState(false);
    const [backupLoading, setBackupLoading] = useState(false);
    const [backupModalOpen, setBackupModalOpen] = useState(false);
    const [backupPw, setBackupPw] = useState('');
    const [backupErr, setBackupErr] = useState('');

    const getHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
    }), [token]);

    const fetchAll = useCallback(async () => {
        try {
            const toJson = async (res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status} – ${res.statusText}`);
                return res.json();
            };
            const [s, u, b, st, uv, ic] = await Promise.all([
                fetch(`${API}/api/admin/stats`, { headers: getHeaders() }).then(toJson),
                fetch(`${API}/api/admin/users`, { headers: getHeaders() }).then(toJson),
                fetch(`${API}/api/bereiche`,    { headers: getHeaders() }).then(toJson),
                fetch(`${API}/api/site-status`).then(r => r.json()),
                fetch(`${API}/api/admin/user-visits`, { headers: getHeaders() }).then(toJson),
                fetch(`${API}/api/admin/invite-code`, { headers: getHeaders() }).then(toJson),
            ]);
            setStats(s);
            setUsers(Array.isArray(u) ? u : []);
            setBereiche(Array.isArray(b) ? b : []);
            setSiteLocked(st.manual_locked ?? st.locked);
            setLegalMissing(Array.isArray(st.legal_missing) ? st.legal_missing : []);
            setUserVisits(Array.isArray(uv) ? uv : []);
            // Wenn Code sich geändert hat (jemand hat registriert) → Rechte zurücksetzen
            setInviteCode(prev => {
                if (prev && ic.code && prev !== ic.code) setInvitePermissions([]);
                return ic.code || prev;
            });
            setError('');
        } catch (err) {
            if (err.message.includes('401')) {
                logout();
                navigate('/login');
                return;
            }
            setError('Fehler: ' + err.message);
            setUsers([]);
            setBereiche([]);
        }
    }, [getHeaders, logout, navigate]);

    // Countdown + Invite-Code + Invite-Permissions nur einmalig beim Laden holen
    useEffect(() => {
        fetch(`${API}/api/countdown`, { headers: getHeaders() })
            .then(r => r.json())
            .then(cd => setCountdown({ title: cd.title || '', target: cd.target || '' }))
            .catch(() => {});
        fetch(`${API}/api/admin/invite-code`, { headers: getHeaders() })
            .then(r => r.json())
            .then(d => setInviteCode(d.code || ''))
            .catch(() => {});
        fetch(`${API}/api/admin/invite-permissions`, { headers: getHeaders() })
            .then(r => r.json())
            .then(d => setInvitePermissions(Array.isArray(d.permissions) ? d.permissions : []))
            .catch(() => {});
        if (isLino) fetchPreviews();
    }, [getHeaders]);

    const fetchPreviews = useCallback(() => {
        if (!isLino) return;
        fetch(`${API}/api/admin/previews`, { headers: getHeaders() })
            .then(r => r.json())
            .then(d => setPreviews(Array.isArray(d) ? d : []))
            .catch(() => {});
    }, [getHeaders, isLino]);

    const fetchUnreadFeedback = useCallback(() => {
        if (!isLino) return;
        fetch(`${API}/api/admin/feedback/unread`, { headers: getHeaders() })
            .then(r => r.json())
            .then(d => {
                const items = Array.isArray(d) ? d : [];
                setUnreadFeedback(prev => {
                    if (items.length > prev.length && prev.length > 0) {
                        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                            try { new Notification('Neue Kunden-Nachricht 💬', { body: items[0].text?.slice(0, 120) }); } catch {}
                        }
                    }
                    if (items.length > 0 && typeof Notification !== 'undefined' && Notification.permission === 'default') {
                        Notification.requestPermission();
                    }
                    return items;
                });
            })
            .catch(() => {});
    }, [getHeaders, isLino]);

    useEffect(() => { fetchUnreadFeedback(); }, [fetchUnreadFeedback]);
    useAutoRefresh(() => { fetchPreviews(); fetchUnreadFeedback(); }, 20000);

    const fetchFeedbackFor = useCallback((pid) => {
        fetch(`${API}/api/admin/previews/${pid}/feedback`, { headers: getHeaders() })
            .then(r => r.json())
            .then(d => setFeedbackByPreview(prev => ({ ...prev, [pid]: Array.isArray(d) ? d : [] })))
            .catch(() => {});
    }, [getHeaders]);

    const toggleFeedbackPanel = (pid) => {
        const nowExpanded = expandedFeedbackId === pid ? null : pid;
        setExpandedFeedbackId(nowExpanded);
        if (nowExpanded) fetchFeedbackFor(pid);
    };

    const setFeedbackDone = async (pid, fid, done) => {
        await fetch(`${API}/api/admin/previews/${pid}/feedback/${fid}/done`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify({ done })
        });
        fetchFeedbackFor(pid);
        fetchPreviews();
        fetchUnreadFeedback();
    };

    const sendReply = async (pid) => {
        const text = (replyText[pid] || '').trim();
        if (!text) return;
        setReplySending(prev => ({ ...prev, [pid]: true }));
        await fetch(`${API}/api/admin/previews/${pid}/chat`, {
            method: 'POST', headers: getHeaders(), body: JSON.stringify({ text })
        });
        setReplyText(prev => ({ ...prev, [pid]: '' }));
        setReplySending(prev => ({ ...prev, [pid]: false }));
        fetchFeedbackFor(pid);
        fetchUnreadFeedback();
    };

    const openPreviewNotif = (p) => {
        setNotifOpen(false);
        setTab('preview');
        setExpandedFeedbackId(p.preview_id);
        fetchFeedbackFor(p.preview_id);
    };

    const regenerateInviteCode = async () => {
        const res = await fetch(`${API}/api/admin/invite-code/regenerate`, { method: 'POST', headers: getHeaders() });
        const d = await res.json();
        setInviteCode(d.code || '');
        setCodeCopied(false);
    };

    const copyInviteCode = () => {
        const doCopy = () => {
            if (navigator.clipboard && window.isSecureContext) {
                return navigator.clipboard.writeText(inviteCode);
            }
            // HTTP fallback
            const el = document.createElement('textarea');
            el.value = inviteCode;
            el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
            document.body.appendChild(el);
            el.focus(); el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            return Promise.resolve();
        };
        doCopy().then(() => {
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        }).catch(() => {});
    };

    // Link inkl. Code kopieren, damit der Kunde ihn nicht mehr selbst eintippen muss —
    // öffnet /preview?code=... direkt in der ablenkungsfreien Ansicht (siehe PreviewPage.jsx).
    const copyPreviewLink = (p) => {
        const link = `${window.location.origin}/preview?code=${encodeURIComponent(p.code)}`;
        const doCopy = () => {
            if (navigator.clipboard && window.isSecureContext) {
                return navigator.clipboard.writeText(link);
            }
            const el = document.createElement('textarea');
            el.value = link;
            el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
            document.body.appendChild(el);
            el.focus(); el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            return Promise.resolve();
        };
        doCopy().then(() => {
            setLinkCopiedId(p.id);
            setTimeout(() => setLinkCopiedId(null), 2000);
        }).catch(() => {});
    };

    const saveInvitePermissions = async () => {
        await fetch(`${API}/api/admin/invite-permissions`, {
            method: 'PUT', headers: getHeaders(),
            body: JSON.stringify({ permissions: invitePermissions })
        });
        setInvitePermSaved(true);
        setTimeout(() => setInvitePermSaved(false), 2000);
    };

    const deactivateUser = async (id) => {
        if (!window.confirm('Benutzer wirklich deaktivieren? Er kann sich nicht mehr einloggen, die Daten bleiben erhalten.')) return;
        await fetch(`${API}/api/admin/users/${id}/deactivate`, { method: 'PUT', headers: getHeaders() });
        fetchAll();
    };

    const reactivateUser = async (id) => {
        await fetch(`${API}/api/admin/users/${id}/reactivate`, { method: 'PUT', headers: getHeaders() });
        fetchAll();
    };

    const permanentDeleteUser = async (id) => {
        if (!window.confirm('Benutzer endgültig löschen? Alle Daten werden unwiderruflich entfernt.')) return;
        await fetch(`${API}/api/admin/users/${id}`, { method: 'DELETE', headers: getHeaders() });
        fetchAll();
    };

    const savePassword = async (id) => {
        const pw = editingPw?.value || '';
        if (pw.length < 6) { setError('Passwort muss mindestens 6 Zeichen haben.'); return; }
        await fetch(`${API}/api/admin/users/${id}/password`, {
            method: 'PUT', headers: getHeaders(),
            body: JSON.stringify({ password: pw })
        });
        setEditingPw(null);
        setPwSaved(id);
        setTimeout(() => setPwSaved(null), 2000);
    };

    useEffect(() => { fetchAll(); }, [fetchAll]);
    useAutoRefresh(fetchAll, 10000);

    const deleteUser = async (id) => {
        if (!window.confirm('User wirklich löschen?')) return;
        try {
            await fetch(`${API}/api/admin/users/${id}`, { method: 'DELETE', headers: getHeaders() });
            fetchAll();
        } catch (err) { setError('Fehler beim Löschen: ' + err.message); }
    };

    const createUser = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const maxLogins = newUser.maxLogins !== '' ? parseInt(newUser.maxLogins) : null;
            await fetch(`${API}/api/admin/users`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ username: newUser.username, password: newUser.password, permissions: newUser.permissions, role: 'custom', max_logins: maxLogins })
            });
            setNewUser({ username: '', password: '', permissions: [], maxLogins: '' });
            fetchAll();
        } catch (err) { setError('Fehler beim Erstellen: ' + err.message); }
        setSaving(false);
    };

    const savePermissions = async (userId) => {
        try {
            await fetch(`${API}/api/admin/users/${userId}/permissions`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ permissions: editingPerms.permissions })
            });
            setEditingPerms(null);
            fetchAll();
        } catch (err) { setError('Fehler beim Speichern: ' + err.message); }
    };

    const toggleBereich = async (key, current) => {
        try {
            await fetch(`${API}/api/admin/bereiche/${key}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ enabled: !current })
            });
            fetchAll();
        } catch (err) { setError('Fehler beim Umschalten: ' + err.message); }
    };

    const toggleSiteLock = async () => {
        const newVal = !siteLocked;
        setSiteLocked(newVal);
        await fetch(`${API}/api/admin/site-locked`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ locked: newVal })
        });
    };

    const saveCountdown = async () => {
        await fetch(`${API}/api/admin/countdown`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ title: countdown.title, target: countdown.target })
        });
        setCountdownSaved(true);
        setTimeout(() => setCountdownSaved(false), 2000);
    };

    const clearCountdown = async () => {
        setCountdown({ title: '', target: '' });
        await fetch(`${API}/api/admin/countdown`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ title: '', target: '' })
        });
    };

    const resetUploadForm = () => {
        setSelectedFile(null);
        setUploadCode('');
        setUploadTitle('');
        setUploadTargetId('');
        setUploadError('');
    };

    const pickFile = (file) => {
        if (!file) return;
        const name = file.name.toLowerCase();
        if (!name.endsWith('.zip') && !name.endsWith('.html') && !name.endsWith('.htm')) {
            setUploadError('Bitte eine .zip oder .html Datei auswählen.');
            return;
        }
        setUploadError('');
        setSelectedFile(file);
    };

    const uploadPreview = async () => {
        if (!selectedFile) { setUploadError('Bitte zuerst eine ZIP-Datei auswählen.'); return; }
        if (uploadMode === 'new' && !uploadCode.trim()) { setUploadError('Bitte einen Code vergeben.'); return; }
        if (uploadMode === 'overwrite' && !uploadTargetId) { setUploadError('Bitte eine Vorschau zum Überschreiben auswählen.'); return; }

        setUploading(true);
        setUploadError('');
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('mode', uploadMode);
        if (uploadMode === 'new') {
            formData.append('code', uploadCode.trim());
            formData.append('title', uploadTitle.trim());
        } else {
            formData.append('preview_id', uploadTargetId);
            if (uploadCode.trim()) formData.append('code', uploadCode.trim());
            if (uploadTitle.trim()) formData.append('title', uploadTitle.trim());
        }

        try {
            const res = await fetch(`${API}/api/admin/previews/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Upload fehlgeschlagen');
            resetUploadForm();
            fetchPreviews();
            setUploadDone(true);
            setTimeout(() => setUploadDone(false), 2500);
        } catch (err) {
            setUploadError(err.message);
        }
        setUploading(false);
    };

    const deletePreview = async (pid) => {
        if (!window.confirm('Diese Vorschau wirklich löschen?')) return;
        await fetch(`${API}/api/admin/previews/${pid}`, { method: 'DELETE', headers: getHeaders() });
        fetchPreviews();
    };

    const setupPreviewApp = async (pid) => {
        setSettingUpId(pid);
        setAppError(prev => ({ ...prev, [pid]: '' }));
        try {
            const res = await fetch(`${API}/api/admin/previews/${pid}/setup`, { method: 'POST', headers: getHeaders() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Setup fehlgeschlagen');
        } catch (err) {
            setAppError(prev => ({ ...prev, [pid]: err.message }));
        }
        setSettingUpId(null);
        fetchPreviews();
    };

    const stopPreviewApp = async (pid) => {
        await fetch(`${API}/api/admin/previews/${pid}/stop`, { method: 'POST', headers: getHeaders() });
        fetchPreviews();
    };

    const resetVisits = async () => {
        if (!window.confirm('Alle Besucherdaten wirklich löschen?')) return;
        if (!window.confirm('Letzte Bestätigung – diese Aktion kann nicht rückgängig gemacht werden.')) return;
        try {
            await fetch(`${API}/api/admin/visits/reset`, { method: 'DELETE', headers: getHeaders() });
            fetchAll();
        } catch (err) { setError('Fehler beim Zurücksetzen: ' + err.message); }
    };

    const downloadBackup = async () => {
        if (!backupPw) { setBackupErr('Bitte Passwort eingeben.'); return; }
        setBackupLoading(true);
        setBackupErr('');
        try {
            const res = await fetch(`${API}/api/admin/backup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ password: backupPw }),
            });
            if (res.status === 401) { setBackupErr('Falsches Passwort.'); setBackupLoading(false); return; }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'portfolio-backup.zip';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setBackupModalOpen(false);
            setBackupPw('');
        } catch (err) {
            setBackupErr('Backup fehlgeschlagen: ' + err.message);
        }
        setBackupLoading(false);
    };

    const inputStyle = {
        background: 'var(--bg-secondary)',
        border: '2px solid var(--border)',
        borderRadius: '10px',
        color: 'var(--text-primary)',
        fontFamily: 'inherit',
        fontSize: '15px',
        padding: '12px 16px',
        width: '100%',
    };

    const tabs = [
        { id: 'users', label: 'User', icon: <Users size={16} /> },
        { id: 'bereiche', label: 'Bereiche', icon: <Layout size={16} /> },
        { id: 'visits', label: 'Besucher', icon: <Eye size={16} /> },
        { id: 'countdown', label: 'Countdown', icon: <Timer size={16} /> },
        ...(isLino ? [{ id: 'preview', label: 'Vorschau', icon: <Lock size={16} /> }] : []),
    ];

    return (
        <div className="section" style={{ minHeight: 'calc(100vh - 140px)' }}>
            <div className="container">
                {backupModalOpen && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                        onClick={() => { if (!backupLoading) setBackupModalOpen(false); }}>
                        <div onClick={e => e.stopPropagation()}
                            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '420px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                <Lock size={18} color="var(--accent)" />
                                <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>Backup bestätigen</h2>
                            </div>
                            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '18px' }}>
                                Das Backup enthält alle Daten und Server-Geheimnisse. Bitte bestätige mit deinem Account-Passwort.
                            </p>
                            <input
                                type="password"
                                autoFocus
                                value={backupPw}
                                onChange={e => { setBackupPw(e.target.value); setBackupErr(''); }}
                                onKeyDown={e => { if (e.key === 'Enter') downloadBackup(); }}
                                placeholder="Dein Passwort"
                                style={{ ...inputStyle, marginBottom: backupErr ? '10px' : '18px' }}
                            />
                            {backupErr && <div style={{ color: '#ff5050', fontSize: '13px', marginBottom: '14px' }}>{backupErr}</div>}
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={downloadBackup} disabled={backupLoading} className="btn-primary" style={{ padding: '11px 20px', width: 'auto' }}>
                                    <Download size={15} /> {backupLoading ? 'Erstelle Backup…' : 'Herunterladen'}
                                </button>
                                <button onClick={() => setBackupModalOpen(false)} disabled={backupLoading} className="icon-button" style={{ padding: '11px 18px', border: '1px solid var(--border)', borderRadius: '8px' }}>
                                    Abbrechen
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                    <Link to="/privat" className="icon-button" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                        <ArrowLeft size={20} />
                        <span style={{ fontWeight: 500 }}>Zurück</span>
                    </Link>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {isFullAdmin && (
                        <button onClick={() => { setBackupPw(''); setBackupErr(''); setBackupModalOpen(true); }} className="icon-button"
                            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 16px', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-secondary)', fontWeight: 600, fontSize: '13px' }}
                            title="Komplettes Backup (Code + Datenbank + Dateien) herunterladen">
                            <Download size={15} /> Backup
                        </button>
                    )}
                    <div style={{ position: 'relative', display: isLino ? 'block' : 'none' }}>
                        <button onClick={() => setNotifOpen(o => !o)} className="icon-button"
                            style={{ position: 'relative', color: 'var(--text-secondary)' }}>
                            <Bell size={20} />
                            {unreadFeedback.length > 0 && (
                                <span style={{
                                    position: 'absolute', top: '-4px', right: '-4px',
                                    background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 700,
                                    borderRadius: '20px', minWidth: '18px', height: '18px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                                }}>
                                    {unreadFeedback.length}
                                </span>
                            )}
                        </button>

                        <AnimatePresence>
                            {notifOpen && (
                                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                                    style={{
                                        position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 50,
                                        width: '320px', maxHeight: '360px', overflowY: 'auto',
                                        background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.3)', padding: '10px',
                                    }}>
                                    <div style={{ fontSize: '13px', fontWeight: 700, padding: '4px 8px 8px', color: 'var(--text-secondary)' }}>
                                        Neue Vorschau-Notizen
                                    </div>
                                    {unreadFeedback.length === 0 ? (
                                        <div style={{ padding: '12px 8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                            Keine neuen Notizen.
                                        </div>
                                    ) : (
                                        unreadFeedback.map(f => (
                                            <button key={f.id} onClick={() => openPreviewNotif(f)}
                                                style={{
                                                    display: 'block', width: '100%', textAlign: 'left', background: 'var(--bg-secondary)',
                                                    border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 12px',
                                                    marginBottom: '6px', cursor: 'pointer', fontFamily: 'inherit',
                                                }}>
                                                <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '3px' }}>
                                                    {f.title || 'Ohne Titel'} <span style={{ color: 'var(--accent)', fontFamily: 'monospace' }}>({f.code})</span>
                                                </div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {f.text}
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    </div>
                </div>

                {error && (
                    <div style={{ padding: '16px 20px', background: 'rgba(255,80,80,0.1)', border: '1px solid #ff5050', borderRadius: '8px', color: '#ff5050', marginBottom: '24px', fontSize: '14px' }}>
                        {error}
                    </div>
                )}

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <h1 className="section-title">Admin Panel</h1>
                    <p className="section-subtitle" style={{ marginBottom: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        Verwaltung
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#4ade80', fontWeight: 600 }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#4ade80', display: 'inline-block', animation: 'pulse 2s infinite' }} />
                            Live
                        </span>
                    </p>

                    <div style={{ display: 'flex', gap: '8px', marginBottom: '40px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {tabs.map(t => (
                            <button key={t.id} onClick={() => setTab(t.id)} style={{
                                display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px',
                                background: tab === t.id ? 'var(--accent)' : 'var(--card-bg)',
                                color: tab === t.id ? '#000' : 'var(--text-primary)',
                                border: `1px solid ${tab === t.id ? 'var(--accent)' : 'var(--border)'}`,
                                borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', transition: 'all 0.2s'
                            }}>
                                {t.icon}{t.label}
                            </button>
                        ))}
                    </div>

                    {/* Bereiche */}
                    {tab === 'bereiche' && (
                        <div style={{ maxWidth: '600px', margin: '0 auto' }}>

                            {/* Auto-Sperre: fehlendes Rechtsdokument */}
                            {isFullAdmin && legalMissing.length > 0 && (
                                <div style={{ padding: '14px 18px', marginBottom: '16px', background: 'rgba(239,68,68,0.08)', border: '1px solid #ef4444', borderRadius: '10px', color: '#ef4444', fontSize: '13px', lineHeight: 1.6 }}>
                                    <strong>Automatische Sperre aktiv.</strong> Diese Rechtsdokumente fehlen: {legalMissing.join(', ')}. Die öffentliche Seite bleibt gesperrt, bis die fehlenden .docx unter <code>/root/portfolio/legal/</code> liegen — Rechtstexte müssen immer erreichbar sein.
                                </div>
                            )}

                            {/* Öffentliche Website sperren – nur Full-Admin */}
                            {isFullAdmin && <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '20px 24px', marginBottom: '24px',
                                background: siteLocked ? 'rgba(255,80,80,0.08)' : 'rgba(251,146,60,0.06)',
                                border: `2px solid ${siteLocked ? '#ff5050' : 'var(--accent)'}`,
                                borderRadius: '12px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <Globe size={22} color={siteLocked ? '#ff5050' : 'var(--accent)'} />
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: '15px' }}>Öffentliche Website</div>
                                        <div style={{ fontSize: '12px', color: siteLocked ? '#ff5050' : 'var(--accent)', marginTop: '2px' }}>
                                            {siteLocked ? '🔒 Gesperrt – Besucher sehen "Bald verfügbar"' : '🌐 Öffentlich zugänglich'}
                                        </div>
                                    </div>
                                </div>
                                <button onClick={toggleSiteLock} style={{
                                    padding: '10px 22px',
                                    background: siteLocked ? 'rgba(251,146,60,0.1)' : 'rgba(255,80,80,0.1)',
                                    border: `1px solid ${siteLocked ? 'var(--accent)' : '#ff5050'}`,
                                    borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '13px',
                                    color: siteLocked ? 'var(--accent)' : '#ff5050', transition: 'all 0.2s'
                                }}>
                                    {siteLocked ? 'Freischalten' : 'Sperren'}
                                </button>
                            </div>}

                            {bereiche.map(b => (
                                <div key={b.key} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '16px 20px', background: 'var(--card-bg)',
                                    border: `1px solid ${b.enabled ? 'var(--accent)' : 'var(--border)'}`,
                                    borderRadius: '8px', marginBottom: '8px', transition: 'all 0.2s'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{b.title}</div>
                                        <div style={{ fontSize: '12px', color: b.enabled ? 'var(--accent)' : 'var(--text-secondary)', marginTop: '2px' }}>
                                            {b.enabled ? 'Aktiv' : 'Inaktiv'}
                                        </div>
                                    </div>
                                    <button onClick={() => toggleBereich(b.key, b.enabled)} style={{
                                        padding: '8px 20px',
                                        background: b.enabled ? 'rgba(255,80,80,0.1)' : 'rgba(251,146,60,0.1)',
                                        border: `1px solid ${b.enabled ? '#ff5050' : 'var(--accent)'}`,
                                        borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '13px',
                                        color: b.enabled ? '#ff5050' : 'var(--accent)', transition: 'all 0.2s'
                                    }}>
                                        {b.enabled ? 'Deaktivieren' : 'Aktivieren'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Besucher */}
                    {tab === 'visits' && stats?.visits && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px', maxWidth: '600px', margin: '0 auto' }}>
                            {/* Anonyme Besucher Gesamt */}
                            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {[
                                    { label: 'Gesamt Besuche', value: stats.visits.total },
                                    { label: 'Öffentlich', value: stats.visits.public },
                                    { label: 'Privat', value: stats.visits.private },
                                ].map((s, i) => (
                                    <div key={i} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '32px 48px', textAlign: 'center', minWidth: '160px' }}>
                                        <div style={{ fontSize: '40px', fontWeight: 800, color: 'var(--accent)' }}>{s.value}</div>
                                        <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '8px' }}>{s.label}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Eingeloggte User-Besuche */}
                            {userVisits.length > 0 && (
                                <div style={{ width: '100%' }}>
                                    <h3 style={{ fontWeight: 700, fontSize: '15px', marginBottom: '12px', color: 'var(--text-primary)' }}>
                                        Nutzer-Besuche
                                    </h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {userVisits.map((uv, i) => (
                                            <div key={uv.username} style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                padding: '12px 18px', background: 'var(--card-bg)',
                                                border: '1px solid var(--border)', borderRadius: '8px'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '20px' }}>#{i + 1}</span>
                                                    <span style={{ fontWeight: 600 }}>{uv.username}</span>
                                                </div>
                                                <span style={{ fontWeight: 700, color: 'var(--accent)', fontSize: '16px' }}>
                                                    {uv.visit_count}×
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <button onClick={resetVisits} style={{
                                padding: '10px 24px', background: 'transparent',
                                border: '1px solid #ff5050', borderRadius: '8px',
                                color: '#ff5050', cursor: 'pointer', fontWeight: 600, fontSize: '14px'
                            }}>
                                Besucherdaten zurücksetzen
                            </button>
                        </div>
                    )}

                    {/* User */}
                    {tab === 'users' && (
                        <div style={{ maxWidth: '700px', margin: '0 auto' }}>

                            {/* Einladungscode + Rechte */}
                            {isFullAdmin && inviteCode && (
                                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px 24px', marginBottom: '28px' }}>
                                    {/* Code-Zeile */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
                                        <div>
                                            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
                                                Einladungscode (Einmalnutzung)
                                            </div>
                                            <div style={{ fontFamily: 'monospace', fontSize: '24px', fontWeight: 700, letterSpacing: '0.18em', color: 'var(--accent)' }}>
                                                {inviteCode}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button onClick={copyInviteCode} style={{
                                                display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer',
                                                background: codeCopied ? 'rgba(74,222,128,0.1)' : 'rgba(251,146,60,0.1)',
                                                border: `1px solid ${codeCopied ? '#4ade80' : 'var(--accent)'}`,
                                                color: codeCopied ? '#4ade80' : 'var(--accent)', fontWeight: 600, fontSize: '13px', transition: 'all 0.2s',
                                            }}>
                                                {codeCopied ? <Check size={14} /> : <Copy size={14} />}
                                                {codeCopied ? 'Kopiert' : 'Kopieren'}
                                            </button>
                                            <button onClick={regenerateInviteCode} style={{
                                                display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer',
                                                background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                                                color: 'var(--text-secondary)', fontWeight: 600, fontSize: '13px',
                                            }}>
                                                <RefreshCw size={14} /> Neu
                                            </button>
                                        </div>
                                    </div>
                                    {/* Rechte für den neuen User */}
                                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>
                                            Rechte beim Registrieren
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                                            <label style={{
                                                display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', padding: '6px 14px',
                                                background: invitePermissions.includes('admin') ? 'rgba(251,146,60,0.15)' : 'var(--bg-secondary)',
                                                border: `2px solid ${invitePermissions.includes('admin') ? 'var(--accent)' : 'var(--border)'}`,
                                                borderRadius: '8px', fontSize: '13px', fontWeight: 600, transition: 'all 0.2s',
                                            }}>
                                                <input type="checkbox" checked={invitePermissions.includes('admin')}
                                                    onChange={() => setInvitePermissions(p => p.includes('admin') ? p.filter(x => x !== 'admin') : ['admin'])}
                                                    style={{ accentColor: 'var(--accent)' }} />
                                                ⭐ Admin-Zugriff
                                            </label>
                                            {!invitePermissions.includes('admin') && bereiche.map(b => (
                                                <label key={b.key} style={{
                                                    display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', padding: '6px 14px',
                                                    background: invitePermissions.includes(b.key) ? 'rgba(251,146,60,0.1)' : 'var(--bg-secondary)',
                                                    border: `2px solid ${invitePermissions.includes(b.key) ? 'var(--accent)' : 'var(--border)'}`,
                                                    borderRadius: '8px', fontSize: '13px', fontWeight: 500, transition: 'all 0.2s',
                                                }}>
                                                    <input type="checkbox" checked={invitePermissions.includes(b.key)}
                                                        onChange={() => setInvitePermissions(p => p.includes(b.key) ? p.filter(x => x !== b.key) : [...p, b.key])}
                                                        style={{ accentColor: 'var(--accent)' }} />
                                                    {b.title}
                                                </label>
                                            ))}
                                        </div>
                                        <button onClick={saveInvitePermissions} style={{
                                            display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 18px', borderRadius: '8px', cursor: 'pointer',
                                            background: invitePermSaved ? 'rgba(74,222,128,0.1)' : 'rgba(251,146,60,0.1)',
                                            border: `1px solid ${invitePermSaved ? '#4ade80' : 'var(--accent)'}`,
                                            color: invitePermSaved ? '#4ade80' : 'var(--accent)', fontWeight: 600, fontSize: '13px', transition: 'all 0.2s',
                                        }}>
                                            {invitePermSaved ? <><Check size={13} /> Gespeichert</> : 'Rechte speichern'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {isFullAdmin && (
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', marginBottom: '32px', overflow: 'hidden' }}>
                                <button type="button" onClick={() => setShowNewUserForm(v => !v)} style={{
                                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '18px 24px', background: 'none', border: 'none', cursor: 'pointer',
                                    fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)',
                                }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Plus size={17} color="var(--accent)" /> Neuer User
                                    </span>
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{showNewUserForm ? '▲' : '▼'}</span>
                                </button>
                            {showNewUserForm && <form onSubmit={createUser} style={{ borderTop: '1px solid var(--border)', padding: '20px 24px 24px' }}>
                                <div style={{ height: 0 }} />
                                <div className="form-group">
                                    <label className="form-label">Benutzername</label>
                                    <input style={inputStyle} value={newUser.username} onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Passwort</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            style={{ ...inputStyle, paddingRight: '48px' }}
                                            value={newUser.password}
                                            onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(v => !v)}
                                            style={{
                                                position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', padding: 0,
                                            }}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Max. Logins <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(leer = unbegrenzt)</span></label>
                                    <input type="number" min="1" style={{ ...inputStyle, width: '140px' }}
                                        value={newUser.maxLogins}
                                        onChange={e => setNewUser(u => ({ ...u, maxLogins: e.target.value }))}
                                        placeholder="z.B. 10" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Zugriff auf Bereiche</label>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        {/* Admin-Zugriff Option */}
                                        <label style={{
                                            display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                                            padding: '8px 16px',
                                            background: newUser.permissions.includes('admin') ? 'rgba(251,146,60,0.15)' : 'var(--bg-secondary)',
                                            border: `2px solid ${newUser.permissions.includes('admin') ? 'var(--accent)' : 'var(--border)'}`,
                                            borderRadius: '8px', transition: 'all 0.2s', fontSize: '14px', fontWeight: 600
                                        }}>
                                            <input type="checkbox" checked={newUser.permissions.includes('admin')}
                                                onChange={() => setNewUser(prev => ({
                                                    ...prev,
                                                    permissions: prev.permissions.includes('admin')
                                                        ? prev.permissions.filter(x => x !== 'admin')
                                                        : ['admin']
                                                }))}
                                                style={{ accentColor: 'var(--accent)' }} />
                                            ⭐ Admin-Zugriff
                                        </label>
                                        {!newUser.permissions.includes('admin') && bereiche.map(b => (
                                            <label key={b.key} style={{
                                                display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                                                padding: '8px 16px',
                                                background: newUser.permissions.includes(b.key) ? 'rgba(251,146,60,0.1)' : 'var(--bg-secondary)',
                                                border: `2px solid ${newUser.permissions.includes(b.key) ? 'var(--accent)' : 'var(--border)'}`,
                                                borderRadius: '8px', transition: 'all 0.2s', fontSize: '14px', fontWeight: 500
                                            }}>
                                                <input type="checkbox" checked={newUser.permissions.includes(b.key)}
                                                    onChange={() => setNewUser(prev => ({
                                                        ...prev,
                                                        permissions: prev.permissions.includes(b.key)
                                                            ? prev.permissions.filter(x => x !== b.key)
                                                            : [...prev.permissions, b.key]
                                                    }))}
                                                    style={{ accentColor: 'var(--accent)' }} />
                                                {b.title}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <button type="submit" className="btn-primary" disabled={saving}>
                                    <Plus size={18} />{saving ? 'Wird erstellt...' : 'User erstellen'}
                                </button>
                            </form>}
                            </div>
                        )}

                            {/* Aktive User */}
                            {users.filter(u => u.active !== 0).map(u => {
                                const isExpanded = editingPerms?.id === u.id || editingPw?.id === u.id;
                                return (
                                <div key={u.id}>
                                    <div style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '16px 20px', background: 'var(--card-bg)',
                                        border: `1px solid ${isExpanded ? 'var(--accent)' : 'var(--border)'}`,
                                        borderRadius: isExpanded ? '12px 12px 0 0' : '8px',
                                        marginBottom: isExpanded ? 0 : '8px'
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>
                                                {u.username}
                                                {u.role === 'admin' && <span style={{ fontSize: '11px', color: 'var(--accent)', marginLeft: '8px', background: 'rgba(251,146,60,0.1)', padding: '2px 8px', borderRadius: '10px' }}>Admin</span>}
                                                {u.role !== 'admin' && JSON.parse(u.permissions || '[]').includes('admin') && <span style={{ fontSize: '11px', color: '#a78bfa', marginLeft: '8px', background: 'rgba(167,139,250,0.1)', padding: '2px 8px', borderRadius: '10px' }}>Sub-Admin</span>}
                                                {pwSaved === u.id && <span style={{ fontSize: '11px', color: '#4ade80', marginLeft: '8px' }}>✓ Passwort geändert</span>}
                                            </div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                                {u.role === 'admin' ? 'Voller Zugriff' : `Bereiche: ${JSON.parse(u.permissions || '[]').join(', ') || 'Kein Zugriff'}`}
                                                {u.max_logins != null && <span style={{ marginLeft: '8px', color: u.login_count >= u.max_logins ? '#ff5050' : 'var(--text-secondary)' }}>· Logins: {u.login_count}/{u.max_logins}</span>}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            {/* Rechte */}
                                            {u.role !== 'admin' && (
                                                <button onClick={() => { setEditingPw(null); setEditingPerms(editingPerms?.id === u.id ? null : { id: u.id, permissions: JSON.parse(u.permissions || '[]') }); }}
                                                    className="icon-button" style={{ fontSize: '12px', fontWeight: 600, padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '6px' }}>
                                                    Rechte
                                                </button>
                                            )}
                                            {/* Passwort */}
                                            {isFullAdmin && (
                                                <button onClick={() => { setEditingPerms(null); setEditingPw(editingPw?.id === u.id ? null : { id: u.id, value: '', show: false }); }}
                                                    className="icon-button" style={{ fontSize: '12px', fontWeight: 600, padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '6px' }}>
                                                    Passwort
                                                </button>
                                            )}
                                            {/* Deaktivieren */}
                                            {u.username !== user?.username && (u.role !== 'admin' || isFullAdmin) && (
                                                <button onClick={() => deactivateUser(u.id)} className="icon-button" style={{ color: '#ff5050' }} title="Deaktivieren">
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Rechte-Panel */}
                                    {editingPerms?.id === u.id && (
                                        <div style={{ padding: '16px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--accent)', borderTop: 'none', borderRadius: '0 0 12px 12px', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 16px', background: editingPerms.permissions.includes('admin') ? 'rgba(251,146,60,0.15)' : 'var(--card-bg)', border: `2px solid ${editingPerms.permissions.includes('admin') ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '8px', transition: 'all 0.2s', fontSize: '14px', fontWeight: 600 }}>
                                                    <input type="checkbox" checked={editingPerms.permissions.includes('admin')} onChange={() => setEditingPerms(prev => ({ ...prev, permissions: prev.permissions.includes('admin') ? prev.permissions.filter(x => x !== 'admin') : ['admin'] }))} style={{ accentColor: 'var(--accent)' }} />
                                                    ⭐ Admin-Zugriff
                                                </label>
                                                {!editingPerms.permissions.includes('admin') && bereiche.map(b => (
                                                    <label key={b.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 16px', background: editingPerms.permissions.includes(b.key) ? 'rgba(251,146,60,0.1)' : 'var(--card-bg)', border: `2px solid ${editingPerms.permissions.includes(b.key) ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '8px', transition: 'all 0.2s', fontSize: '14px', fontWeight: 500 }}>
                                                        <input type="checkbox" checked={editingPerms.permissions.includes(b.key)} onChange={() => setEditingPerms(prev => ({ ...prev, permissions: prev.permissions.includes(b.key) ? prev.permissions.filter(x => x !== b.key) : [...prev.permissions, b.key] }))} style={{ accentColor: 'var(--accent)' }} />
                                                        {b.title}
                                                    </label>
                                                ))}
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button onClick={() => savePermissions(u.id)} className="btn-primary" style={{ padding: '10px 24px', width: 'auto' }}>Speichern</button>
                                                <button onClick={() => setEditingPerms(null)} className="icon-button" style={{ padding: '10px 16px' }}>Abbrechen</button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Passwort-Panel */}
                                    {editingPw?.id === u.id && (
                                        <div style={{ padding: '16px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--accent)', borderTop: 'none', borderRadius: '0 0 12px 12px', marginBottom: '8px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Neues Passwort</div>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <div style={{ position: 'relative', flex: 1 }}>
                                                    <input
                                                        type={editingPw.show ? 'text' : 'password'}
                                                        placeholder="Mindestens 6 Zeichen"
                                                        value={editingPw.value}
                                                        onChange={e => setEditingPw(p => ({ ...p, value: e.target.value }))}
                                                        style={{ ...inputStyle, paddingRight: '44px', margin: 0 }}
                                                        autoFocus
                                                    />
                                                    <button type="button" onClick={() => setEditingPw(p => ({ ...p, show: !p.show }))} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 0, display: 'flex' }}>
                                                        {editingPw.show ? <EyeOff size={16} /> : <Eye size={16} />}
                                                    </button>
                                                </div>
                                                <button onClick={() => savePassword(u.id)} className="btn-primary" style={{ padding: '10px 20px', width: 'auto', flexShrink: 0 }}>Speichern</button>
                                                <button onClick={() => setEditingPw(null)} className="icon-button" style={{ padding: '10px 14px' }}>Abbrechen</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                );
                            })}

                            {/* Deaktivierte User */}
                            {users.some(u => u.active === 0) && (
                                <div style={{ marginTop: '24px' }}>
                                    <button onClick={() => setShowDeactivated(v => !v)} style={{
                                        display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none',
                                        cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600, padding: '6px 0', marginBottom: '10px',
                                    }}>
                                        <span style={{ fontSize: '10px' }}>{showDeactivated ? '▲' : '▼'}</span>
                                        Deaktivierte User ({users.filter(u => u.active === 0).length})
                                    </button>
                                    {showDeactivated && users.filter(u => u.active === 0).map(u => (
                                        <div key={u.id} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '14px 20px', background: 'var(--card-bg)', opacity: 0.55,
                                            border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '8px',
                                        }}>
                                            <div>
                                                <div style={{ fontWeight: 600, color: 'var(--text-secondary)', textDecoration: 'line-through' }}>{u.username}</div>
                                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>Deaktiviert</div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <button onClick={() => reactivateUser(u.id)} style={{
                                                    fontSize: '12px', fontWeight: 600, padding: '6px 14px', borderRadius: '6px', cursor: 'pointer',
                                                    background: 'rgba(74,222,128,0.1)', border: '1px solid #4ade80', color: '#4ade80',
                                                }}>Wiederherstellen</button>
                                                <button onClick={() => permanentDeleteUser(u.id)} className="icon-button" style={{ color: '#ff5050' }} title="Endgültig löschen">
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Countdown */}
                    {tab === 'countdown' && (
                        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px 32px' }}>
                                <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '24px' }}>Countdown konfigurieren</div>

                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>Titel</div>
                                    <input
                                        type="text"
                                        placeholder="z.B. Launch in..."
                                        value={countdown.title}
                                        onChange={e => setCountdown(prev => ({ ...prev, title: e.target.value }))}
                                        style={inputStyle}
                                    />
                                </div>

                                <div style={{ marginBottom: '28px' }}>
                                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>Zieldatum & Uhrzeit</div>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <input
                                            type="date"
                                            value={countdown.target ? countdown.target.split('T')[0] : ''}
                                            onChange={e => setCountdown(prev => ({ ...prev, target: `${e.target.value}T${prev.target?.split('T')[1] || '12:00'}` }))}
                                            style={{ ...inputStyle, flex: 2 }}
                                        />
                                        <input
                                            type="time"
                                            value={countdown.target ? (countdown.target.split('T')[1] || '12:00') : '12:00'}
                                            onChange={e => setCountdown(prev => ({ ...prev, target: `${prev.target?.split('T')[0] || ''}T${e.target.value}` }))}
                                            style={{ ...inputStyle, flex: 1 }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button onClick={saveCountdown} className="btn-primary" style={{ padding: '12px 28px', width: 'auto' }}>
                                        {countdownSaved ? '✓ Gespeichert' : 'Speichern'}
                                    </button>
                                    {countdown.target && (
                                        <button onClick={clearCountdown} className="icon-button" style={{ padding: '12px 16px', color: '#ff5050' }}>
                                            Entfernen
                                        </button>
                                    )}
                                </div>
                            </div>

                            {countdown.target && (
                                <div style={{ marginTop: '16px', padding: '14px 20px', background: 'rgba(251,146,60,0.06)', border: '1px solid var(--accent)', borderRadius: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                    ⏱ Countdown läuft bis <strong style={{ color: 'var(--accent)' }}>{new Date(countdown.target).toLocaleString('de-DE')}</strong>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Preview */}
                    {tab === 'preview' && (
                        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px 32px', marginBottom: '24px' }}>
                                <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>Neue Vorschau hochladen</div>
                                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: 1.5 }}>
                                    ZIP-Datei (mit index.html + Assets) oder eine einzelne HTML-Datei hier hineinziehen. Unter <strong style={{ color: 'var(--accent)' }}>/preview</strong> kann jeder mit dem passenden Code die jeweilige Seite ansehen.
                                </div>

                                {/* Mode toggle */}
                                <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '4px', width: 'fit-content' }}>
                                    {[['new', 'Neue Vorschau'], ['overwrite', 'Vorhandene überschreiben']].map(([key, label]) => (
                                        <button key={key} onClick={() => { setUploadMode(key); setUploadError(''); }}
                                            style={{ padding: '8px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer',
                                                fontSize: '13px', fontWeight: 600, fontFamily: 'inherit',
                                                background: uploadMode === key ? 'var(--card-bg)' : 'transparent',
                                                color: uploadMode === key ? 'var(--text-primary)' : 'var(--text-secondary)',
                                                boxShadow: uploadMode === key ? '0 1px 3px rgba(0,0,0,0.3)' : 'none' }}>
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                {uploadMode === 'new' ? (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>Code für diese Vorschau</div>
                                        <input
                                            type="text"
                                            placeholder="z.B. PREVIEW2026"
                                            value={uploadCode}
                                            onChange={e => setUploadCode(e.target.value.toUpperCase())}
                                            style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '0.05em' }}
                                        />
                                    </div>
                                ) : (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>Welche Vorschau überschreiben?</div>
                                        <select value={uploadTargetId} onChange={e => setUploadTargetId(e.target.value)}
                                            style={{ ...inputStyle, cursor: 'pointer' }}>
                                            <option value="">— auswählen —</option>
                                            {previews.map(p => (
                                                <option key={p.id} value={p.id}>{p.title || 'Ohne Titel'} ({p.code})</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>
                                        Titel {uploadMode === 'overwrite' ? '(optional ändern)' : '(optional)'}
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="z.B. Neue Website"
                                        value={uploadTitle}
                                        onChange={e => setUploadTitle(e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>

                                {uploadMode === 'overwrite' && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>Code ändern (optional)</div>
                                        <input
                                            type="text"
                                            placeholder="Leer lassen = Code bleibt gleich"
                                            value={uploadCode}
                                            onChange={e => setUploadCode(e.target.value.toUpperCase())}
                                            style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '0.05em' }}
                                        />
                                    </div>
                                )}

                                {/* Drag & Drop Zone */}
                                <label
                                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
                                    style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        gap: '10px', padding: '36px 20px', borderRadius: '12px', cursor: 'pointer',
                                        border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`,
                                        background: dragOver ? 'rgba(251,146,60,0.06)' : 'var(--bg-secondary)',
                                        transition: 'all 0.15s', marginBottom: '20px',
                                    }}>
                                    <input type="file" accept=".zip,.html,.htm" style={{ display: 'none' }}
                                        onChange={e => pickFile(e.target.files?.[0])} />
                                    {selectedFile ? (
                                        <>
                                            <FileArchive size={28} color="var(--accent)" />
                                            <div style={{ fontSize: '14px', fontWeight: 600 }}>{selectedFile.name}</div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB — klicken zum Ändern</div>
                                        </>
                                    ) : (
                                        <>
                                            <Upload size={28} color="var(--text-secondary)" />
                                            <div style={{ fontSize: '14px', fontWeight: 600 }}>ZIP oder HTML-Datei hier ablegen</div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>oder klicken zum Auswählen</div>
                                        </>
                                    )}
                                </label>

                                {uploadError && (
                                    <div style={{ padding: '10px 14px', marginBottom: '16px', background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: '8px', color: '#ff5050', fontSize: '13px' }}>
                                        {uploadError}
                                    </div>
                                )}

                                <button onClick={uploadPreview} disabled={uploading} className="btn-primary" style={{ padding: '12px 28px', width: 'auto' }}>
                                    {uploading ? 'Lädt hoch…' : uploadDone ? '✓ Hochgeladen' : 'Hochladen'}
                                </button>
                            </div>

                            {/* List of existing previews */}
                            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                                Vorhandene Vorschauen ({previews.length})
                            </div>
                            {previews.length === 0 ? (
                                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', fontSize: '13px' }}>
                                    Noch keine Vorschau hochgeladen.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {previews.map(p => {
                                        const openCount = p.open_feedback_count || 0;
                                        const isExpanded = expandedFeedbackId === p.id;
                                        const items = feedbackByPreview[p.id] || [];
                                        return (
                                        <div key={p.id} style={{ background: 'var(--card-bg)',
                                            border: `1px solid ${openCount > 0 ? '#ef4444' : 'var(--border)'}`, borderRadius: '10px', padding: '14px 18px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                                                <div style={{ flex: 1, minWidth: '140px' }}>
                                                    <div style={{ fontSize: '14px', fontWeight: 700 }}>{p.title || 'Ohne Titel'}</div>
                                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                                        {p.created_at ? new Date(p.created_at).toLocaleString('de-DE') : ''}
                                                    </div>
                                                </div>
                                                <div style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, color: 'var(--accent)',
                                                    background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.3)', borderRadius: '8px', padding: '4px 10px' }}>
                                                    {p.code}
                                                </div>
                                                <button onClick={() => toggleFeedbackPanel(p.id)}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px',
                                                        cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                                                        border: `1px solid ${openCount > 0 ? '#ef4444' : 'var(--border)'}`,
                                                        background: openCount > 0 ? 'rgba(239,68,68,0.1)' : 'var(--bg-secondary)',
                                                        color: openCount > 0 ? '#ef4444' : 'var(--text-secondary)' }}>
                                                    <MessageSquare size={14} />
                                                    Chat{openCount > 0 ? ` (${openCount} neu)` : ''}
                                                    {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                                </button>
                                                {p.folder && (
                                                    // Über /preview öffnen statt direkt die Rohdatei -> Chat + PC/Handy-Umschalter bleiben nutzbar.
                                                    <a href={`/preview?code=${encodeURIComponent(p.code)}`}
                                                        target="_blank" rel="noopener"
                                                        className="icon-button" style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                                                        <ExternalLink size={14} /> Ansehen
                                                    </a>
                                                )}
                                                {p.folder && (
                                                    <button onClick={() => copyPreviewLink(p)} title="Link mit Code zum Teilen kopieren"
                                                        className="icon-button" style={{ display: 'flex', alignItems: 'center', gap: '5px',
                                                            color: linkCopiedId === p.id ? 'var(--accent)' : 'var(--text-secondary)', fontSize: '13px' }}>
                                                        {linkCopiedId === p.id ? <Check size={14} /> : <Copy size={14} />}
                                                        {linkCopiedId === p.id ? 'Kopiert' : 'Link teilen'}
                                                    </button>
                                                )}
                                                <button onClick={() => deletePreview(p.id)} className="icon-button" style={{ color: '#ef4444' }}>
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>

                                            {/* Backend-App Steuerung */}
                                            {p.app_stack && p.app_stack !== 'none' && (
                                                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>
                                                            <Server size={13} />
                                                            {p.app_stack === 'node' ? 'Node.js' : 'Python'} erkannt
                                                        </div>
                                                        <span style={{
                                                            fontSize: '11px', fontWeight: 700, padding: '2px 9px', borderRadius: '20px',
                                                            color: p.app_status === 'running' ? '#4ade80' : p.app_status === 'idle' ? '#60a5fa' : p.app_status === 'building' ? '#f59e0b' : p.app_status === 'error' ? '#ef4444' : 'var(--text-secondary)',
                                                            background: p.app_status === 'running' ? 'rgba(74,222,128,0.1)' : p.app_status === 'idle' ? 'rgba(96,165,250,0.1)' : p.app_status === 'building' ? 'rgba(245,158,11,0.1)' : p.app_status === 'error' ? 'rgba(239,68,68,0.1)' : 'var(--bg-secondary)',
                                                            border: `1px solid ${p.app_status === 'running' ? '#4ade80' : p.app_status === 'idle' ? '#60a5fa' : p.app_status === 'building' ? '#f59e0b' : p.app_status === 'error' ? '#ef4444' : 'var(--border)'}`,
                                                        }}>
                                                            {{ none: 'Nicht eingerichtet', building: 'Build läuft…', running: 'Läuft', idle: 'Pausiert (spart Ressourcen)', stopped: 'Gestoppt', error: 'Fehler' }[p.app_status] || p.app_status}
                                                        </span>

                                                        {(p.app_status === 'none' || p.app_status === 'stopped' || p.app_status === 'error') && (
                                                            <button onClick={() => setupPreviewApp(p.id)} disabled={settingUpId === p.id}
                                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px',
                                                                    border: '1px solid var(--accent)', background: 'rgba(251,146,60,0.1)', color: 'var(--accent)',
                                                                    cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600 }}>
                                                                {settingUpId === p.id
                                                                    ? <motion.span style={{ display: 'flex' }} animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}><Loader2 size={13} /></motion.span>
                                                                    : <Power size={13} />}
                                                                {settingUpId === p.id ? 'Baut…' : 'Einrichten'}
                                                            </button>
                                                        )}
                                                        {(p.app_status === 'running' || p.app_status === 'idle') && (
                                                            <button onClick={() => stopPreviewApp(p.id)}
                                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px',
                                                                    border: '1px solid #ef4444', background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                                                                    cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600 }}>
                                                                <Power size={13} /> Stoppen
                                                            </button>
                                                        )}
                                                        {p.app_log && (
                                                            <button onClick={() => setExpandedLogId(expandedLogId === p.id ? null : p.id)}
                                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px',
                                                                    border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                                                                    cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600 }}>
                                                                <Terminal size={13} /> Log
                                                                {expandedLogId === p.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {appError[p.id] && (
                                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#ef4444' }}>{appError[p.id]}</div>
                                                    )}
                                                    {expandedLogId === p.id && p.app_log && (
                                                        <pre style={{ marginTop: '8px', padding: '10px 12px', background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                                                            borderRadius: '8px', fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap',
                                                            maxHeight: '200px', overflowY: 'auto', margin: '8px 0 0', fontFamily: 'monospace' }}>
                                                            {p.app_log}
                                                        </pre>
                                                    )}
                                                </div>
                                            )}

                                            <AnimatePresence>
                                                {isExpanded && (
                                                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                                        style={{ overflow: 'hidden' }}>
                                                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                                                            {/* Chat messages */}
                                                            <div ref={el => { if (el) el.scrollTop = el.scrollHeight; }} style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto', marginBottom: '12px', padding: '2px 0' }}>
                                                                {items.length === 0 ? (
                                                                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center', padding: '16px 0' }}>Noch keine Nachrichten.</div>
                                                                ) : (
                                                                    items.map(f => {
                                                                        const isAdmin = f.sender === 'admin';
                                                                        const isCustomer = !isAdmin;
                                                                        return (
                                                                            <div key={f.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isAdmin ? 'flex-end' : 'flex-start', gap: '2px' }}>
                                                                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', letterSpacing: '0.3px', paddingLeft: isAdmin ? 0 : '4px', paddingRight: isAdmin ? '4px' : 0 }}>
                                                                                    {isAdmin ? 'Du' : 'Kunde'} · {f.created_at ? new Date(f.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                                                                                </div>
                                                                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', flexDirection: isAdmin ? 'row-reverse' : 'row' }}>
                                                                                    <div style={{
                                                                                        maxWidth: '75%', padding: '8px 11px',
                                                                                        borderRadius: isAdmin ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                                                                                        background: isAdmin ? 'var(--accent)' : 'var(--bg-secondary)',
                                                                                        color: isAdmin ? '#000' : 'var(--text-primary)',
                                                                                        fontSize: '13px', lineHeight: 1.5,
                                                                                        border: isCustomer ? '1px solid var(--border)' : 'none',
                                                                                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                                                                    }}>
                                                                                        {f.text}
                                                                                    </div>
                                                                                    {isCustomer && (
                                                                                        <button onClick={() => setFeedbackDone(p.id, f.id, !f.done)} title={f.done ? 'Als unerledigt markieren' : 'Als erledigt markieren'}
                                                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, opacity: 0.6 }}>
                                                                                            {f.done
                                                                                                ? <CheckSquare size={14} color="#4ade80" />
                                                                                                : <Square size={14} color="var(--text-secondary)" />}
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}
                                                            </div>
                                                            {/* Reply input */}
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                <input
                                                                    value={replyText[p.id] || ''}
                                                                    onChange={e => setReplyText(prev => ({ ...prev, [p.id]: e.target.value }))}
                                                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(p.id); } }}
                                                                    placeholder="Antwort schreiben…"
                                                                    style={{
                                                                        flex: 1, background: 'var(--bg-secondary)', border: '1.5px solid var(--border)',
                                                                        borderRadius: '8px', color: 'var(--text-primary)', fontFamily: 'inherit',
                                                                        fontSize: '13px', padding: '8px 12px', outline: 'none',
                                                                    }}
                                                                />
                                                                <button onClick={() => sendReply(p.id)} disabled={replySending[p.id] || !(replyText[p.id] || '').trim()}
                                                                    style={{
                                                                        background: (replyText[p.id] || '').trim() && !replySending[p.id] ? 'var(--accent)' : 'var(--bg-secondary)',
                                                                        border: '1px solid var(--border)', borderRadius: '8px',
                                                                        cursor: (replyText[p.id] || '').trim() ? 'pointer' : 'default',
                                                                        padding: '8px 14px', color: (replyText[p.id] || '').trim() && !replySending[p.id] ? '#000' : 'var(--text-secondary)',
                                                                        fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, transition: 'all 0.15s', flexShrink: 0,
                                                                    }}>
                                                                    {replySending[p.id] ? '…' : 'Senden'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
}